import { type TypeModel } from '../common/dataTypes';
import { mainApi } from '../utils/api';

export type BaseInput = {
  orgId: number;
  projectId: number;
};

export type ByIdInput = BaseInput & {
  modelId: number;
};

export type CreateModelInput = BaseInput & {
  data: {
    name: string;
    description?: string;
    dataset: number;
    project: number;
    copy_scenarios_from?: number;
    copy_dataset: boolean;
    new_dataset_name?: string;
    new_dataset_description?: string;
  };
};

export type UpdateModelInput = ByIdInput & {
  data: {
    name?: string;
    description?: string;
    predefined_expenses?: number[];
    userdefined_expenses?: number[];
    retirement_age?: number;
    driver_config?: TypeModel['driver_config'];
    seat_lock_period?: number;
    training_lock_period?: number;
    dataset?: number;
  };
};

export type GetModelsInput = BaseInput;

export type GetModelsByDatasetIdInput = BaseInput & {
  datasetId: number;
};

export type GetModelByIdInput = ByIdInput;

export type GetModelOutputInput = ByIdInput;

export type RunModelOutputInput = ByIdInput & {
  data: {
    filters?: {
      aircrafts: string[];
      airports: string[];
    };
  };
};

export type DeleteModelInput = ByIdInput;

export type CloneModelInput = ByIdInput;

export type GetModelDataTableSchemasInput = ByIdInput;

export type GetModelIsRunningInput = ByIdInput;

export type RunModelComparisonInput = BaseInput & {
  data: {
    modelA: string;
    modelB: string;
  };
};

export type GetModelComparisonOutputInput = BaseInput & {
  modelA: string;
  modelB: string;
};

export type GetModelOutputWithActualDataInput = ByIdInput;

export type GetModelComparisonIsRunningInput = BaseInput & {
  modelA: string;
  modelB: string;
};

export type ExportModelOutputInput = ByIdInput & {
  dataName: string;
};

const getModels = ({ orgId, projectId }: GetModelsInput) =>
  mainApi.get(`organizations/${orgId}/projects/${projectId}/models/`);

const cloneModel = async ({ orgId, projectId, modelId }: CloneModelInput) =>
  mainApi.post(
    `organizations/${orgId}/projects/${projectId}/models/${modelId}/clone/`
  );

const getModelsByDatasetId = ({
  orgId,
  projectId,
  datasetId,
}: GetModelsByDatasetIdInput) =>
  mainApi.get(
    `organizations/${orgId}/projects/${projectId}/models/?dataset=${datasetId}`
  );

const getModelById = ({ orgId, projectId, modelId }: GetModelByIdInput) =>
  mainApi.get(
    `organizations/${orgId}/projects/${projectId}/models/${modelId}/`
  );

const getModelOutput = ({ modelId, orgId, projectId }: GetModelOutputInput) =>
  mainApi.get(
    `organizations/${orgId}/projects/${projectId}/models/${modelId}/output/`
  );

const getModelOutputWithActualData = ({
  modelId,
  orgId,
  projectId,
}: GetModelOutputWithActualDataInput) =>
  mainApi.get(
    `organizations/${orgId}/projects/${projectId}/models/${modelId}/output_with_actual_data/`
  );

const runModelOutput = ({
  modelId,
  orgId,
  projectId,
  data,
}: RunModelOutputInput) =>
  mainApi.post(
    `organizations/${orgId}/projects/${projectId}/models/${modelId}/run/`,
    data
  );

const createModel = ({ orgId, projectId, data }: CreateModelInput) =>
  mainApi.post(`organizations/${orgId}/projects/${projectId}/models/`, data);

const getModelIsRunning = ({
  orgId,
  projectId,
  modelId,
}: GetModelIsRunningInput) =>
  mainApi.get(
    `organizations/${orgId}/projects/${projectId}/models/${modelId}/is_running/`
  );

const deleteModelById = ({ orgId, projectId, modelId }: DeleteModelInput) =>
  mainApi.delete(
    `organizations/${orgId}/projects/${projectId}/models/${modelId}/`
  );

const updateModel = ({ orgId, projectId, modelId, data }: UpdateModelInput) =>
  mainApi.patch(
    `organizations/${orgId}/projects/${projectId}/models/${modelId}/`,
    data
  );

const runModelComparison = ({
  projectId,
  orgId,
  data,
}: RunModelComparisonInput) =>
  mainApi.post(
    `organizations/${orgId}/projects/${projectId}/run_model_comparison/`,
    {
      model_a: data.modelA,
      model_b: data.modelB,
    }
  );

const getModelComparisonOutput = ({
  projectId,
  orgId,
  modelA,
  modelB,
}: GetModelComparisonOutputInput) =>
  mainApi.get(
    `organizations/${orgId}/projects/${projectId}/get_model_comparison_result/?model_a=${modelA}&model_b=${modelB}`
  );

const getModelComparisonIsRunning = ({
  orgId,
  projectId,
  modelA,
  modelB,
}: GetModelComparisonIsRunningInput) =>
  mainApi.get(
    `organizations/${orgId}/projects/${projectId}/is_running_comparison/?model_a=${modelA}&model_b=${modelB}`
  );

const exportModelOutput = ({
  modelId,
  orgId,
  projectId,
  dataName,
}: ExportModelOutputInput) =>
  mainApi.get(
    `organizations/${orgId}/projects/${projectId}/models/${modelId}/export_output_to_excel/?data_name=${dataName}`,
    {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      responseType: 'blob',
    }
  );

export {
  cloneModel,
  createModel,
  deleteModelById,
  getModelById,
  getModelComparisonIsRunning,
  getModelComparisonOutput,
  getModelIsRunning,
  getModelOutput,
  getModelOutputWithActualData,
  getModels,
  getModelsByDatasetId,
  exportModelOutput,
  runModelComparison,
  runModelOutput,
  updateModel,
};
